// Ship manifest for the set picker: "Ships in {n} parts".
// The shopper ticks a set that ships in more than one dispatch - this card
// replaces the batch pane and states, per piece, when it leaves the warehouse.
//
// State machine on `skullette:set:change` (detail: { kind, members }):
//   solo / set all in-stock      -> manifest hidden, batch pane untouched
//   set all pre-order, ONE window -> pane stays the storyteller; a single
//                                    joined sentence lands in its foot note
//   set all pre-order, MIXED windows (owner decision 260802: same as mixed)
//   set mixed now + pre           -> manifest shown, pane eclipsed via class
//
// Facts are read verbatim from the picker island strings (preE) and the
// anchor attrs - the exact strings the cart line `_preorder_eta` property
// carries, so pane, manifest and cart line never disagree. Batch codes are
// production order numbers and NEVER render here (batch-card doctrine).

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six'];
const JOIN_SENTENCE = 'Your matching pieces ship with this batch.';

function ctx() {
  const picker = document.querySelector('[data-set-picker]');
  if (!picker) return null;
  const section = picker.closest('.shopify-section, section') || document;
  return {
    picker,
    shell: section.querySelector('[data-ship-manifest]'),
    pane: section.querySelector('.sk-porder'),
  };
}

function thumbSrc(picker, idx) {
  const sel = idx == null ? '.sk-set__row--anchor .sk-set__thumb' : `[data-set-row="${idx}"] .sk-set__thumb`;
  return picker.querySelector(sel)?.getAttribute('src') || '';
}

// "Batch 2 · open" - derived from the pane's open ledger row, never invented
function batchSubline(pane) {
  const open = pane?.querySelector('.sk-porder__row.is-open');
  const n = open?.querySelector('.sk-porder__n')?.textContent?.trim();
  if (!n) return '';
  const st = open.querySelector('.sk-porder__st')?.textContent?.trim();
  return `<span class="sk-manifest__batch">Batch ${n}${st ? ` · ${st.toLowerCase()}` : ''}</span>`;
}

// What the last render produced - the re-assert observer needs to know the
// difference between "shell hidden because the pane tells the story" and
// "shell hidden because a morph wiped it" (mistaking the former for the
// latter re-renders forever: render itself mutates the pane foot).
let lastMode = 'none'; // 'none' | 'join' | 'manifest'

function render(detail) {
  const c = ctx();
  if (!c?.shell) return;
  const { picker, shell, pane } = c;
  lastMode = 'none';

  // clear-then-inject: every render starts from the untouched base state, so
  // repeated tick/untick can never stack sentences or stale rows
  shell.hidden = true;
  shell.innerHTML = '';
  pane?.classList.remove('sk-porder--eclipsed');
  pane?.querySelector('[data-set-join]')?.remove();

  const members = detail?.members ?? [];
  if (!members.length) return; // solo - the page reads exactly as before

  const anchorPre = picker.hasAttribute('data-anchor-pre');
  const items = [
    {
      short: picker.getAttribute('data-anchor-short') || 'This piece',
      pre: anchorPre,
      eta: picker.getAttribute('data-anchor-eta') || '',
      img: thumbSrc(picker, null),
      isAnchor: true,
    },
    ...members.map((m) => ({
      short: m.short,
      pre: !!m.preB,
      eta: m.preE || '',
      img: thumbSrc(picker, m.idx),
      isAnchor: false,
    })),
  ];

  const preItems = items.filter((i) => i.pre);
  if (!preItems.length) return; // everything ships now - nothing to explain

  const windows = [...new Set(preItems.map((i) => i.eta))];
  const anyNow = items.length > preItems.length;

  if (!anyNow && windows.length === 1) {
    // one batch carries the whole set: the pane keeps telling the story
    const foot = pane?.querySelector('.sk-porder__foot');
    if (foot) {
      const join = document.createElement('span');
      join.setAttribute('data-set-join', '');
      join.textContent = ` ${JOIN_SENTENCE}`;
      foot.appendChild(join);
      lastMode = 'join';
    }
    return;
  }

  // mixed dispatch (or all-pre across different windows): the manifest takes
  // the pane's spot. Ships-now pieces lead - the good news first.
  const parts = windows.length + (anyNow ? 1 : 0);
  const partsWord = NUMBER_WORDS[parts] || String(parts);
  const ordered = [...items.filter((i) => !i.pre), ...items.filter((i) => i.pre)];

  const rows = ordered
    .map((i) => {
      const tag = i.pre
        ? `<span class="sk-manifest__tag sk-manifest__tag--later">${i.eta || 'Ships with next batch'}</span>`
        : '<span class="sk-manifest__tag sk-manifest__tag--now">Ships now</span>';
      const sub = i.isAnchor && i.pre ? batchSubline(pane) : '';
      return `<div class="sk-manifest__row">${
        i.img ? `<img class="sk-manifest__thumb" src="${i.img}" alt="" width="44" height="58" loading="lazy">` : ''
      }<span class="sk-manifest__name">${i.short}${sub}</span>${tag}</div>`;
    })
    .join('');

  // essence (V2): when the anchor itself is the pre-order piece, its pane's
  // ledger + note are cloned VERBATIM - scarcity copy stays single-source in
  // the pane, the manifest claims no word of its own. The joined sentence was
  // already removed above, so the clone is always the base pane.
  let essence = '';
  if (anchorPre && pane) {
    const table = pane.querySelector('.sk-porder__table');
    const foot = pane.querySelector('.sk-porder__foot');
    if (table || foot) {
      essence = `<div class="sk-manifest__essence">${table?.outerHTML ?? ''}${foot?.outerHTML ?? ''}</div>`;
    }
  }

  shell.innerHTML = `<div class="sk-manifest__title">Ships in ${partsWord} parts</div>${rows}${essence}`;
  shell.hidden = false;
  pane?.classList.add('sk-porder--eclipsed');
  lastMode = 'manifest';
}

let lastDetail = null;
document.addEventListener('skullette:set:change', (event) => {
  lastDetail = event.detail;
  render(lastDetail);
});

// Variant switches can morph the form markup around the shell and wipe what
// the last render produced while members are still ticked - re-assert from
// the last selection. Only a render whose OUTPUT went missing re-runs, so a
// quiet page never re-renders (a hidden shell is the normal resting state of
// the join and none modes, not evidence of a wipe).
let queued = false;
new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    if (!lastDetail?.members?.length) return;
    const c = ctx();
    if (!c?.shell) return;
    const wiped =
      (lastMode === 'manifest' && (c.shell.hidden || !c.shell.innerHTML)) ||
      (lastMode === 'join' && !c.pane?.querySelector('[data-set-join]'));
    if (wiped) render(lastDetail);
  });
}).observe(document.body, { childList: true, subtree: true });
